import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { renderIcon } from '@/shared/utils/iconMapper';
import { MenuItem } from './Sidebar'; // ✅ Importar MenuItem
import styles from './SidebarItem.module.css';

/**
 * Props del componente SidebarItem
 * @param id - ID único del item
 * @param nombre - Nombre del item a mostrar
 * @param url - URL de navegación (si es null, es un contenedor)
 * @param icono - Nombre del icono de lucide-react o componente icono
 * @param children - Subitems anidados (si tiene)
 * @param childrenData - Datos de MenuItem[] para calcular hasActiveChild
 * @param isCollapsed - Si el sidebar está colapsado (modo mini)
 * @param level - Nivel de anidación (0 = raíz, 1 = primer nivel, etc.)
 * @param isOpen - Si el item está expandido (controlado desde el padre)
 * @param onToggle - Callback para manejar el toggle del item
 * @param parentId - ID del padre del item (null si es raíz)
 */
export interface SidebarItemProps {
  id: number;
  nombre: string;
  url: string | null;
  icono?: string | null;
  children?: ReactNode;
  childrenData?: MenuItem[]; // ✅ NUEVO: Datos de hijos para calcular hasActiveChild
  hasChildren?: boolean; // ✅ NUEVO: indica si tiene hijos basado en datos, no en children renderizados
  isCollapsed?: boolean;
  isHovered?: boolean;
  level?: number;
  isOpen?: boolean;
  onToggle?: (itemId: number, parentId: number | null) => void;
  onNavigate?: () => void; // ✅ NUEVO: Callback cuando se navega a una URL
  parentId?: number | null;
}

/**
 * Verifica recursivamente si algún hijo (desde datos) tiene una URL activa
 * @param items - Items del menú
 * @param pathname - Ruta actual
 */
const isChildActiveFromData = (items: MenuItem[] | undefined, pathname: string): boolean => {
  if (!items || items.length === 0) return false;

  for (const item of items) {
    // Si el item tiene URL y coincide con pathname
    if (item.url && (pathname === item.url || pathname.startsWith(item.url + '/'))) {
      return true;
    }
    // Verificar recursivamente en hijos
    if (item.children && isChildActiveFromData(item.children, pathname)) {
      return true;
    }
  }

  return false;
};

/**
 * Verifica recursivamente si algún elemento hijo tiene una URL que coincide con la ruta actual
 * @param children - Elementos hijos del sidebar item
 * @param pathname - Ruta actual del navegador
 * @returns true si algún hijo está activo
 */
const isChildActive = (children: ReactNode, pathname: string): boolean => {
  if (!children) return false;

  const checkChild = (child: any): boolean => {
    if (!child) return false;

    // Si es un array de children, verificar cada uno
    if (Array.isArray(child)) {
      return child.some(checkChild);
    }

    // Si tiene props.url, verificar si coincide con la ruta actual
    if (child.props?.url) {
      const childUrl = child.props.url;
      if (pathname === childUrl || pathname.startsWith(childUrl + '/')) {
        return true;
      }
    }

    // Verificar recursivamente en children anidados
    if (child.props?.children) {
      return isChildActive(child.props.children, pathname);
    }

    return false;
  };

  return checkChild(children);
};

export const SidebarItem = ({
  id,
  nombre,
  url,
  icono,
  children,
  childrenData, // ✅ NUEVO
  hasChildren: hasChildrenProp, // ✅ NUEVO: recibir del padre
  isCollapsed = false,
  isHovered: _isHovered = false, // Parámetro recibido pero lógica usa isCollapsed directamente
  level = 0,
  isOpen = false,
  onToggle,
  onNavigate, // ✅ NUEVO
  parentId = null,
}: SidebarItemProps) => {
  const location = useLocation();

  // ✅ FIX: Usar prop si existe, sino calcular de children
  // Esto evita que hasChildren sea false cuando children no se renderizan por shouldHideChildren
  const hasChildren = hasChildrenProp ?? Boolean(children);

  // Determinar si el item está activo basado en la ruta actual
  // 1. Si tiene URL propia, verificar coincidencia directa
  //    - Coincidencia exacta: siempre es match
  //    - Coincidencia por prefijo (startsWith): solo si NO tiene hijos
  //    Esto evita que padres se resalten cuando solo un hijo está activo
  const isDirectMatch = url ? (
    location.pathname === url ||
    (!hasChildren && location.pathname.startsWith(url + '/'))
  ) : false;

  // 2. Si tiene hijos, verificar si algún hijo está activo (solo para padres)
  //    Primero intentar con childrenData (datos del menú), si no está disponible usar children (ReactNode)
  const hasActiveChild = hasChildren ? (
    childrenData
      ? isChildActiveFromData(childrenData, location.pathname)
      : isChildActive(children, location.pathname)
  ) : false;

  // 3. Lógica de resaltado:
  //    ESTADO DEL SIDEBAR:
  //    - Expandido: isCollapsed=false, isHovered=false
  //    - Colapsado sin hover: isCollapsed=true, isHovered=false  
  //    - Colapsado con hover: isCollapsed=false, isHovered=true
  //    
  //    REGLA: Resaltar padre cuando el sidebar está en configuración colapsada (detectado por isHovered)
  //           PERO solo cuando los items están en modo mini (isCollapsed=true, es decir SIN hover)
  //    Por lo tanto: 
  //    - Si isCollapsed=true (colapsado sin hover): resaltar padre
  //    - Si isHovered=true (colapsado con hover): NO resaltar padre, se ven los hijos
  const shouldHighlightParent = isCollapsed && hasActiveChild;
  const isActive = isDirectMatch || shouldHighlightParent;

  const handleToggle = (e: React.MouseEvent) => {
    if (onToggle) {
      // Always notify parent for accordion behavior
      onToggle(id, parentId);

      // Only prevent default if item has children (keep link navigation)
      if (hasChildren) {
        e.preventDefault();
      }
    }
  };

  // Renderizar el icono desde la BD
  const iconElement = renderIcon(icono, 20, styles.icon);

  const itemContent = (
    <>
      {iconElement || <div className={styles.iconPlaceholder}>{nombre.charAt(0)}</div>}
      <span className={`${styles.itemText} ${isCollapsed ? styles.hidden : ''}`}>
        {nombre}
      </span>
      {hasChildren && (
        <ChevronRight
          size={16}
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''} ${isCollapsed ? styles.hidden : ''}`}
        />
      )}
    </>
  );

  const itemClasses = [
    styles.navItem,
    hasChildren ? styles.navItemSubmenu : '',
    isOpen ? styles.navItemOpen : '',
    isActive ? styles.active : '',
    isCollapsed ? styles.collapsed : '',
  ].filter(Boolean).join(' ');

  return (
    <li className={itemClasses}>
      {url && !hasChildren ? (
        <Link
          to={url}
          className={styles.navLink}
          onClick={() => {
            onToggle?.(id, parentId);
            onNavigate?.(); // ✅ Cerrar sidebar móvil al navegar
          }}
          style={{ paddingLeft: `${1.3 + level * 1.5}rem` }}
        >
          {itemContent}
        </Link>
      ) : (
        <button
          type="button"
          className={styles.navLink}
          onClick={handleToggle}
          aria-expanded={isOpen}
          style={{ paddingLeft: `${1.3 + level * 1.5}rem` }}
        >
          {itemContent}
        </button>
      )}

      {hasChildren && (
        <ul
          className={`${styles.navGroupSub} ${isOpen ? styles.show : ''}`}
        >
          {children}
        </ul>
      )}
    </li>
  );
};

